import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Box, Chip, Paper, Stack, Typography } from '@mui/material'
import { useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useDealData } from 'api/aws/deals/useDealData'
import { useEntitiesFromSearch } from 'api/search'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import { buildMasterAgreements } from 'utils/agreementVersioning'

/**
 * Compact horizontal strip of all versions in this deal's master agreement.
 * Each version is clickable and navigates to the same sub-route the user is
 * currently viewing on the other version — so an audit comparison of
 * v1 vs v2 on the Agreement → Heartbeat Preview screen stays on Heartbeat
 * Preview when toggling versions.
 *
 * Renders nothing when:
 *   - There's no lineage data yet (deals/products still loading)
 *   - The deal has only one version (lineage strip would be noise)
 *
 * Props:
 *   - `subPath`: the route segment to navigate to on a click (default 'details').
 *      Examples: 'details', 'agreement', 'review', 'versions'.
 *   - `variant`: visual treatment — 'inline' for embedding in a tab's content,
 *      'footer' for a fixed-bottom translucent bar inside a tab that scrolls.
 */

type Variant = 'inline' | 'footer'

/**
 * CSS custom property carrying the rendered height of the fixed footer strip.
 * Published on <html> while a footer variant is mounted, 0px otherwise. The
 * strip is `position: fixed`, so it sits outside layout flow and would cover
 * the bottom of whichever tab is mounted. Tabs reserve room with
 * LINEAGE_FOOTER_OFFSET rather than hard-coding a guess, which also stays
 * correct when the chip row wraps to two lines on a narrow screen.
 */
export const LINEAGE_FOOTER_HEIGHT_VAR = '--deal-lineage-footer-height'
export const LINEAGE_FOOTER_OFFSET = `var(${LINEAGE_FOOTER_HEIGHT_VAR}, 0px)`

interface DealLineageStripProps {
  subPath?: string
  variant?: Variant
}

const DealLineageStrip: React.FC<DealLineageStripProps> = ({
  subPath = 'details',
  variant = 'inline',
}) => {
  const { PK, SK } = useParams<{ PK: string; SK: string }>()
  const dealId = SK?.split('DEAL_')?.[1]
  const { deal } = useDealData(PK, SK, dealId)
  const { deals, products: allProducts } = useEntitiesFromSearch()
  const navigate = useNavigate()
  const location = useLocation()

  const footerRef = useRef<HTMLDivElement | null>(null)

  const versions = useMemo(() => {
    if (!PK || !deal?.deal_uuid) return []
    const orgDeals = deals?.filter(d => d?.PK === PK) ?? []
    const orgProducts = allProducts?.filter(p => p?.PK === PK) ?? []
    const { agreements } = buildMasterAgreements(orgDeals, orgProducts)
    const match =
      (deal.masterAgreementId
        ? agreements.find(a => a.masterAgreementId === deal.masterAgreementId)
        : null) ?? agreements.find(a => a.versions.some(v => v.deal?.deal_uuid === deal.deal_uuid))
    return match?.versions ?? []
  }, [PK, deal?.deal_uuid, deal?.masterAgreementId, deals, allProducts])

  const isFooter = variant === 'footer'
  const hasLineage = versions.length > 1

  // Publish the strip's height so the mounted tab can hold its content clear
  // of it. Runs above the early return below to stay an unconditional hook.
  useEffect(() => {
    const root = document.documentElement
    const reset = (): void => {
      root.style.setProperty(LINEAGE_FOOTER_HEIGHT_VAR, '0px')
    }
    if (!isFooter || !hasLineage) {
      reset()
      return reset
    }
    const el = footerRef.current
    if (!el) return reset
    const publish = (): void => {
      root.style.setProperty(LINEAGE_FOOTER_HEIGHT_VAR, `${Math.round(el.getBoundingClientRect().height)}px`)
    }
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(el)
    return () => {
      observer.disconnect()
      reset()
    }
  }, [isFooter, hasLineage])

  if (!hasLineage) return null

  const handleNavigate = (targetDealUuid: string | undefined): void => {
    if (!targetDealUuid) return
    // Preserve current search params (e.g. ?sub=heartbeat for the Agreement
    // tab's heartbeat preview) so a version switch lands on the same view.
    const search = location.search ?? ''
    navigate(`/deals/${PK}/DEAL_${targetDealUuid}/${subPath}${search}`)
  }

  return (
    <Paper
      ref={footerRef}
      elevation={isFooter ? 6 : 0}
      variant={isFooter ? 'elevation' : 'outlined'}
      sx={
        isFooter
          ? {
              // Fixed to the viewport so the strip sits in the same place
              // regardless of which sub-tab is mounted. The Heartbeat preview
              // has its own internal scroll container which broke the
              // previous `position: sticky` anchor between tabs.
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: theme => theme.zIndex.appBar - 1,
              px: 2,
              py: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
              borderRadius: 0,
              bgcolor: theme => theme.palette.background.paper,
              backdropFilter: 'blur(8px)',
            }
          : { p: 1.25 }
      }
    >
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Typography
          variant="caption"
          sx={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'text.secondary',
            mr: 0.5,
          }}
        >
          Deal Lineage
        </Typography>
        {versions.map((v, i) => {
          const isCurrent = v.deal?.deal_uuid === deal?.deal_uuid
          const label = isCurrent ? 'Viewing' : i === 0 ? 'Master' : 'Revision'
          return (
            <Box key={v.deal?.deal_uuid ?? i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {i > 0 && (
                <ArrowForwardIcon
                  sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.5, flexShrink: 0 }}
                />
              )}
              <LineageNode
                deal={v.deal}
                label={label}
                version={v.semanticVersion}
                isCurrent={isCurrent}
                onClick={!isCurrent ? () => handleNavigate(v.deal?.deal_uuid) : undefined}
              />
            </Box>
          )
        })}
      </Stack>
    </Paper>
  )
}

interface LineageNodeProps {
  deal: { name?: string; dealStatus?: string; stage?: string } | undefined
  label: string
  version?: string
  isCurrent?: boolean
  onClick?: () => void
}

/**
 * Compact node card. Mirrors DealChainNode in DealInfoCard but tuned for
 * the strip — narrower, single-line, optimized for many siblings in a row.
 */
const LineageNode: React.FC<LineageNodeProps> = ({
  deal,
  label,
  version,
  isCurrent = false,
  onClick,
}) => {
  const statusColor = renderColorByString(deal?.dealStatus)
  const labelColor = label === 'Viewing' ? 'text.primary' : renderColorByString(label)
  return (
    <Box
      onClick={onClick}
      sx={{
        border: '1px solid',
        borderColor: isCurrent ? 'primary.main' : 'divider',
        borderWidth: isCurrent ? 2 : 1,
        borderRadius: 1,
        px: 0.75,
        py: 0.5,
        minWidth: 110,
        maxWidth: 180,
        ...(onClick && { cursor: 'pointer', '&:hover': { opacity: 0.75, borderColor: 'primary.light' } }),
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 0.25 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 600, color: labelColor }}>{label}</Typography>
        {version && (
          <Chip
            label={`v${version}`}
            size="small"
            sx={{
              height: 14,
              fontSize: 9,
              fontWeight: 700,
              fontFamily: 'monospace',
              bgcolor: isCurrent ? 'primary.main' : 'action.selected',
              color: isCurrent ? 'primary.contrastText' : 'text.primary',
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        )}
      </Stack>
      <Typography noWrap title={deal?.name} sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary' }}>
        {deal?.name}
      </Typography>
      <Stack direction="row" alignItems="center" gap={0.5}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusColor, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 10, color: statusColor, fontWeight: 500 }}>
          {deal?.dealStatus || deal?.stage}
        </Typography>
      </Stack>
    </Box>
  )
}

export default DealLineageStrip
